import { useEffect, useState } from "react";
import { PHONE_QUERY } from "../hooks/useIsPhone";

/**
 * "Add to Home Screen" on phones. iOS has no install API: the banner shows
 * the Share → Add to Home Screen steps. Chrome on Android fires
 * `beforeinstallprompt` once the manifest qualifies; we keep the event and
 * the banner's Install button opens the browser's own install dialog.
 */

export type InstallPlatform = "ios" | "android" | "other";

/** iPadOS reports itself as a Mac; touch points give it away. */
export const detectInstallPlatform = (
  userAgent: string,
  maxTouchPoints = 0,
): InstallPlatform => {
  if (
    /iPhone|iPad|iPod/.test(userAgent) ||
    (/Macintosh/.test(userAgent) && maxTouchPoints > 1)
  ) {
    return "ios";
  }
  if (/Android/i.test(userAgent)) return "android";
  return "other";
};

/** Opened from the Home Screen icon, not in a browser tab. */
export const isStandalone = () =>
  typeof window !== "undefined" &&
  ((typeof window.matchMedia === "function" &&
    window.matchMedia("(display-mode: standalone)").matches) ||
    (window.navigator as Navigator & { standalone?: boolean }).standalone ===
      true);

// The banner's close button hides it for a week
export const SNOOZE_MS = 7 * 24 * 60 * 60 * 1000;
const DISMISSED_AT_KEY = "installBannerDismissedAt";

export const isSnoozed = (dismissedAt: string | null, now: number) => {
  if (!dismissedAt) return false;
  const at = new Date(dismissedAt).getTime();
  return Number.isFinite(at) && now - at < SNOOZE_MS;
};

export const readDismissedAt = () => {
  try {
    return localStorage.getItem(DISMISSED_AT_KEY);
  } catch {
    return null;
  }
};

export const snoozeInstallBanner = () => {
  try {
    localStorage.setItem(DISMISSED_AT_KEY, new Date().toISOString());
  } catch {
    // private mode: the banner just comes back on the next visit
  }
};

type BeforeInstallPromptEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
};

let deferredPrompt: BeforeInstallPromptEvent | null = null;
let installed = false;
const listeners = new Set<() => void>();
const notify = () => listeners.forEach((listener) => listener());

/**
 * Called once at startup: Chrome may fire the event before the banner
 * mounts. On a phone its own mini-infobar is held back in favour of ours.
 */
export const captureInstallPrompt = () => {
  if (typeof window === "undefined") return;
  window.addEventListener("beforeinstallprompt", (event) => {
    if (!window.matchMedia?.(PHONE_QUERY).matches) return;
    event.preventDefault();
    deferredPrompt = event as BeforeInstallPromptEvent;
    notify();
  });
  window.addEventListener("appinstalled", () => {
    deferredPrompt = null;
    installed = true;
    notify();
  });
};

/** The browser's install dialog, when Chrome has offered one. */
export const useInstallPrompt = () => {
  const [, rerender] = useState(0);

  useEffect(() => {
    const listener = () => rerender((n) => n + 1);
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  }, []);

  const prompt = async () => {
    const event = deferredPrompt;
    if (!event) return "unavailable" as const;
    // An event can prompt only once
    deferredPrompt = null;
    notify();
    await event.prompt();
    const { outcome } = await event.userChoice;
    return outcome;
  };

  return { canPrompt: deferredPrompt !== null, installed, prompt };
};
